import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  RefreshCw, ChevronDown, ChevronRight, Search, Globe,
  CheckCircle, Settings2, Tag, Mail, ShieldCheck, Save,
  Plus, Trash2, GripVertical, ChevronUp, Users, Clock, Lock,
} from 'lucide-react';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OPS = ['add', 'renew', 'transfer', 'restore'];
const OP_LABELS = { add: 'Add', renew: 'Renew', transfer: 'Transfer', restore: 'Restore' };
const OP_COLORS = { add: 'text-blue-600', renew: 'text-green-600', transfer: 'text-violet-600', restore: 'text-amber-600' };

function fmt(n) {
  return '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

// ── Tab 1: TLD Pricing Management ─────────────────────────────────────────────
function TldPricingTab() {
  const [tlds, setTlds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [showPopular, setShowPopular] = useState('all');
  const [expandedTld, setExpandedTld] = useState(null);
  const [expandedView, setExpandedView] = useState('operations');
  const [editingTld, setEditingTld] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [bulkProfit, setBulkProfit] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [saving, setSaving] = useState('');

  const fetchPricing = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/domain-pricing`);
      setTlds(res.data || []);
    } catch {
      toast.error('Failed to load domain pricing');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPricing(); }, [fetchPricing]);

  const handleSyncRC = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/admin/domain-pricing/sync-rc`);
      toast.success(`RC Sync: ${res.data.synced} TLDs updated, ${res.data.failed} failed`);
      fetchPricing();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'RC sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleBulkProfit = async () => {
    const pct = parseFloat(bulkProfit);
    if (isNaN(pct) || pct < 0) { toast.error('Enter valid profit %'); return; }
    setBulkSaving(true);
    try {
      const res = await axios.put(`${API}/admin/domain-pricing-bulk/profit`, {
        profit_pct: pct, operations: OPS,
      });
      toast.success(res.data.message);
      setBulkProfit('');
      fetchPricing();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk update failed');
    } finally {
      setBulkSaving(false);
    }
  };

  const startEdit = (tld) => {
    const doc = tlds.find(t => t.tld === tld);
    if (!doc) return;
    setEditingTld(tld);
    setEditForm({
      markup_cost_add:      doc.markup_cost?.add      ?? 0,
      markup_cost_renew:    doc.markup_cost?.renew     ?? 0,
      markup_cost_transfer: doc.markup_cost?.transfer  ?? 0,
      markup_cost_restore:  doc.markup_cost?.restore   ?? 0,
      profit_pct_add:      doc.profit_pct.add,
      profit_pct_renew:    doc.profit_pct.renew,
      profit_pct_transfer: doc.profit_pct.transfer,
      profit_pct_restore:  doc.profit_pct.restore,
      yearly: doc.yearly_pricing.map(y => ({ ...y, markup_cost: y.markup_cost ?? 0 })),
    });
  };

  const handleSave = async (tld) => {
    setSaving(tld);
    try {
      await axios.put(`${API}/admin/domain-pricing/${tld}`, {
        markup_cost_add:      parseFloat(editForm.markup_cost_add)      || 0,
        markup_cost_renew:    parseFloat(editForm.markup_cost_renew)     || 0,
        markup_cost_transfer: parseFloat(editForm.markup_cost_transfer)  || 0,
        markup_cost_restore:  parseFloat(editForm.markup_cost_restore)   || 0,
        profit_pct_add:      parseFloat(editForm.profit_pct_add),
        profit_pct_renew:    parseFloat(editForm.profit_pct_renew),
        profit_pct_transfer: parseFloat(editForm.profit_pct_transfer),
        profit_pct_restore:  parseFloat(editForm.profit_pct_restore),
        yearly_pricing: editForm.yearly,
      });
      toast.success(`.${tld} pricing saved`);
      setEditingTld(null);
      fetchPricing();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving('');
    }
  };

  const handleToggleEnabled = async (tld, enabled) => {
    try {
      await axios.put(`${API}/admin/domain-pricing/${tld}`, { enabled: !enabled });
      toast.success(`.${tld} ${!enabled ? 'enabled' : 'disabled'}`);
      fetchPricing();
    } catch {
      toast.error('Update failed');
    }
  };

  const filtered = tlds.filter(t => {
    const matchSearch = !search || t.tld.toLowerCase().includes(search.toLowerCase());
    const matchPop = showPopular === 'all' || (showPopular === 'popular' && t.popular);
    return matchSearch && matchPop;
  });

  const stats = {
    total:   tlds.length,
    enabled: tlds.filter(t => t.enabled).length,
    synced:  tlds.filter(t => t.rc_synced).length,
  };

  return (
    <div className="space-y-5">
      {/* Stats + actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-4">
          {[
            { label: 'Total TLDs',   val: stats.total,   color: 'text-blue-600' },
            { label: 'Enabled',       val: stats.enabled, color: 'text-green-600' },
            { label: 'RC Synced',     val: stats.synced,  color: 'text-violet-600' },
          ].map(s => (
            <div key={s.label} className="bg-slate-50 rounded-xl px-4 py-2 text-center">
              <p className={`text-xl font-bold ${s.color}`}>{s.val}</p>
              <p className="text-xs text-slate-500">{s.label}</p>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5">
            <Input
              type="number" min="0" max="500"
              placeholder="Profit % for all"
              value={bulkProfit}
              onChange={e => setBulkProfit(e.target.value)}
              className="w-36 h-9 text-sm"
              data-testid="bulk-profit-input"
            />
            <Button size="sm" onClick={handleBulkProfit} disabled={bulkSaving || !bulkProfit}
              className="h-9 bg-slate-800 text-white" data-testid="bulk-profit-btn">
              {bulkSaving ? 'Saving…' : 'Set All'}
            </Button>
          </div>
          <Button variant="outline" size="sm" onClick={handleSyncRC} disabled={syncing}
            className="h-9" data-testid="sync-rc-btn">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing…' : 'Sync from RC'}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input placeholder="Search TLD…" value={search} onChange={e => setSearch(e.target.value)}
            className="pl-8 h-9 w-40 text-sm" />
        </div>
        <div className="flex gap-1 bg-slate-100 p-0.5 rounded-lg">
          {[['all', 'All TLDs'], ['popular', 'Popular']].map(([val, lbl]) => (
            <button key={val} onClick={() => setShowPopular(val)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${showPopular === val ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              {lbl}
            </button>
          ))}
        </div>
        <p className="text-xs text-slate-400 ml-2">{filtered.length} TLDs</p>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin h-7 w-7 rounded-full border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <div className="grid text-xs font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 px-5 py-3 border-b border-slate-100"
            style={{ gridTemplateColumns: '24px 90px 1fr 1fr 1fr 1fr 100px 80px' }}>
            <span></span>
            <span>TLD</span>
            <span>Add Cost / Profit %</span>
            <span>Renew Cost / Profit %</span>
            <span>Transfer Cost</span>
            <span>Restore Cost</span>
            <span>Selling Price</span>
            <span>Status</span>
          </div>

          {filtered.length === 0 ? (
            <div className="py-12 text-center text-slate-400 text-sm">No TLDs found</div>
          ) : (
            filtered.map(t => {
              const isExpanded = expandedTld === t.tld;
              const isEditing  = editingTld === t.tld;

              return (
                <div key={t.tld} className="border-b border-slate-50 last:border-0">
                  <div
                    className={`grid px-5 py-3 items-center hover:bg-slate-50 transition-colors text-sm ${isExpanded ? 'bg-blue-50' : ''}`}
                    style={{ gridTemplateColumns: '24px 90px 1fr 1fr 1fr 1fr 100px 80px' }}
                    data-testid={`tld-row-${t.tld}`}
                  >
                    <button onClick={() => setExpandedTld(isExpanded ? null : t.tld)}
                      className="text-slate-400 hover:text-blue-500">
                      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                    <div>
                      <span className="font-mono font-semibold text-slate-800">.{t.tld}</span>
                      {t.popular && (
                        <span className="ml-1.5 text-[9px] font-bold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">HOT</span>
                      )}
                    </div>
                    <div className="text-xs">
                      <p className="text-slate-500">{fmt(t.cost_price.add)}</p>
                      <p className="text-green-600 font-medium">{t.profit_pct.add}%</p>
                    </div>
                    <div className="text-xs">
                      <p className="text-slate-500">{fmt(t.cost_price.renew)}</p>
                      <p className="text-green-600 font-medium">{t.profit_pct.renew}%</p>
                    </div>
                    <div className="text-xs"><p className="text-slate-500">{fmt(t.cost_price.transfer)}</p></div>
                    <div className="text-xs"><p className="text-slate-500">{fmt(t.cost_price.restore)}</p></div>
                    <div className="text-xs font-semibold text-slate-800">{fmt(t.selling_price.add)}/yr</div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => handleToggleEnabled(t.tld, t.enabled)}
                        className={`w-9 h-5 rounded-full transition-all relative ${t.enabled ? 'bg-green-500' : 'bg-slate-300'}`}
                        data-testid={`tld-toggle-${t.tld}`}>
                        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${t.enabled ? 'left-4' : 'left-0.5'}`} />
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="bg-blue-50 border-t border-blue-100 px-8 py-5">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex gap-1 bg-white border border-slate-200 p-0.5 rounded-lg">
                          {[['operations', 'Operations (ADD/RENEW/TRANSFER/RESTORE)'], ['yearly', '1–10 Year Pricing']].map(([v, l]) => (
                            <button key={v} onClick={() => setExpandedView(v)}
                              className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${expandedView === v ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                              {l}
                            </button>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          {isEditing ? (
                            <>
                              <Button size="sm" onClick={() => setEditingTld(null)} variant="outline" className="h-7 text-xs">Cancel</Button>
                              <Button size="sm" onClick={() => handleSave(t.tld)} disabled={saving === t.tld}
                                className="h-7 text-xs bg-blue-600 text-white" data-testid={`save-tld-${t.tld}`}>
                                {saving === t.tld ? 'Saving…' : 'Save Changes'}
                              </Button>
                            </>
                          ) : (
                            <Button size="sm" variant="outline" onClick={() => startEdit(t.tld)}
                              className="h-7 text-xs gap-1" data-testid={`edit-tld-${t.tld}`}>
                              <Settings2 className="h-3.5 w-3.5" /> Edit Prices
                            </Button>
                          )}
                        </div>
                      </div>

                      {expandedView === 'operations' && (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          {OPS.map(op => (
                            <div key={op} className="bg-white rounded-xl border border-slate-200 p-4">
                              <p className={`text-xs font-bold uppercase tracking-wide mb-3 ${OP_COLORS[op]}`}>
                                {OP_LABELS[op]}
                              </p>
                              {isEditing ? (
                                <div className="space-y-2">
                                  <div>
                                    <label className="text-[10px] text-slate-400 font-medium">RC Cost (₹)</label>
                                    <div className="h-7 px-2 py-1 rounded bg-slate-100 text-xs text-slate-500 mt-0.5 flex items-center">
                                      {fmt(t.cost_price[op])}
                                    </div>
                                  </div>
                                  <div>
                                    <label className="text-[10px] text-slate-500">Markup Cost (₹)</label>
                                    <Input type="number" min="0"
                                      value={editForm[`markup_cost_${op}`]}
                                      onChange={e => setEditForm(p => ({ ...p, [`markup_cost_${op}`]: e.target.value }))}
                                      className="h-7 text-xs mt-0.5" placeholder="0" />
                                  </div>
                                  <div>
                                    <label className="text-[10px] text-slate-500">Profit %</label>
                                    <Input type="number" min="0"
                                      value={editForm[`profit_pct_${op}`]}
                                      onChange={e => setEditForm(p => ({ ...p, [`profit_pct_${op}`]: e.target.value }))}
                                      className="h-7 text-xs mt-0.5" />
                                  </div>
                                  <div className="bg-green-50 rounded-lg px-2 py-1.5">
                                    <p className="text-[10px] text-green-600">Selling Price</p>
                                    <p className="text-sm font-bold text-green-700">
                                      {fmt((parseFloat(t.cost_price[op] || 0) + parseFloat(editForm[`markup_cost_${op}`] || 0)) * (1 + parseFloat(editForm[`profit_pct_${op}`] || 0) / 100))}
                                    </p>
                                    <p className="text-[9px] text-green-500 mt-0.5">(RC + Markup) × (1 + Profit%)</p>
                                  </div>
                                </div>
                              ) : (
                                <div className="space-y-1.5">
                                  <div className="flex justify-between text-xs">
                                    <span className="text-slate-400">RC Cost</span>
                                    <span className="font-medium text-slate-500">{fmt(t.cost_price[op])}</span>
                                  </div>
                                  <div className="flex justify-between text-xs">
                                    <span className="text-slate-500">Markup</span>
                                    <span className="font-medium">{fmt(t.markup_cost?.[op] ?? 0)}</span>
                                  </div>
                                  <div className="flex justify-between text-xs">
                                    <span className="text-slate-500">Profit</span>
                                    <span className="font-medium text-green-600">{t.profit_pct[op]}%</span>
                                  </div>
                                  <div className="border-t border-slate-100 pt-1.5 flex justify-between text-xs">
                                    <span className="font-semibold text-slate-700">Selling</span>
                                    <span className="font-bold text-slate-800">{fmt(t.selling_price[op])}</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {expandedView === 'yearly' && (
                        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="bg-slate-50 border-b border-slate-100">
                                <th className="px-4 py-2.5 text-left font-semibold text-slate-600">Years</th>
                                <th className="px-4 py-2.5 text-right font-semibold text-slate-500">RC Cost</th>
                                <th className="px-4 py-2.5 text-right font-semibold text-slate-600">Markup Cost (₹)</th>
                                <th className="px-4 py-2.5 text-right font-semibold text-slate-600">Profit %</th>
                                <th className="px-4 py-2.5 text-right font-semibold text-slate-600">Selling Price</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(isEditing ? editForm.yearly : t.yearly_pricing).map((row, i) => {
                                const mk  = isEditing ? (parseFloat(editForm.yearly[i]?.markup_cost) || 0) : (row.markup_cost ?? 0);
                                const pp  = isEditing ? (parseFloat(editForm.yearly[i]?.profit_pct)  || 0) : row.profit_pct;
                                const cp  = row.cost_price;
                                const sp  = Math.round((cp + mk) * (1 + pp / 100));
                                return (
                                  <tr key={row.year} className={`border-b border-slate-50 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                                    <td className="px-4 py-2.5 font-semibold text-slate-700">
                                      {row.year} {row.year === 1 ? 'Year' : 'Years'}
                                    </td>
                                    <td className="px-4 py-2.5 text-right text-slate-400 font-mono">{fmt(cp)}</td>
                                    <td className="px-4 py-2.5 text-right">
                                      {isEditing ? (
                                        <input type="number" min="0"
                                          value={editForm.yearly[i]?.markup_cost ?? 0}
                                          onChange={e => {
                                            const yearly = [...editForm.yearly];
                                            yearly[i] = { ...yearly[i], markup_cost: parseFloat(e.target.value) || 0 };
                                            setEditForm(p => ({ ...p, yearly }));
                                          }}
                                          className="w-24 text-right border border-slate-200 rounded px-2 py-1 text-xs focus:border-blue-400 outline-none" />
                                      ) : fmt(row.markup_cost ?? 0)}
                                    </td>
                                    <td className="px-4 py-2.5 text-right">
                                      {isEditing ? (
                                        <input type="number" min="0" max="500"
                                          value={editForm.yearly[i]?.profit_pct ?? 0}
                                          onChange={e => {
                                            const yearly = [...editForm.yearly];
                                            yearly[i] = { ...yearly[i], profit_pct: parseFloat(e.target.value) || 0 };
                                            setEditForm(p => ({ ...p, yearly }));
                                          }}
                                          className="w-16 text-right border border-slate-200 rounded px-2 py-1 text-xs focus:border-blue-400 outline-none" />
                                      ) : (
                                        <span className="text-green-600 font-medium">{row.profit_pct}%</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-2.5 text-right font-bold text-slate-800">{fmt(sp)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                          {isEditing && (
                            <p className="px-4 py-2 text-[10px] text-slate-400 bg-slate-50 border-t border-slate-100">
                              Selling Price = (RC Cost + Markup Cost) × (1 + Profit%)
                            </p>
                          )}
                        </div>
                      )}

                      {t.rc_synced && (
                        <p className="text-[10px] text-slate-400 mt-3 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3 text-green-500" /> RC live prices synced
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ── Tab 2: Available TLDs & Pricing (Public View) ──────────────────────────────
function AvailableTldsTab() {
  const [tlds, setTlds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showPopular, setShowPopular] = useState('all');

  useEffect(() => {
    axios.get(`${API}/domain-pricing/public`)
      .then(r => setTlds(r.data || []))
      .catch(() => toast.error('Failed to load TLD pricing'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = tlds.filter(t => {
    const matchSearch = !search || t.tld.toLowerCase().includes(search.toLowerCase());
    const matchPop = showPopular === 'all' || (showPopular === 'popular' && t.popular);
    return matchSearch && matchPop;
  });

  const yearCols = [1, 2, 3, 5];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-700">Public-Facing TLD Price List</h3>
          <p className="text-xs text-slate-400 mt-0.5">Selling prices as seen by partners and clients. Only enabled TLDs are shown.</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input placeholder="Search TLD…" value={search} onChange={e => setSearch(e.target.value)}
              className="pl-8 h-9 w-40 text-sm" />
          </div>
          <div className="flex gap-1 bg-slate-100 p-0.5 rounded-lg">
            {[['all', 'All'], ['popular', 'Popular']].map(([val, lbl]) => (
              <button key={val} onClick={() => setShowPopular(val)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${showPopular === val ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                {lbl}
              </button>
            ))}
          </div>
          <span className="text-xs text-slate-400">{filtered.length} TLDs</span>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin h-7 w-7 rounded-full border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="px-5 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">TLD</th>
                {yearCols.map(y => (
                  <th key={y} className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wide">
                    {y} Yr{y > 1 ? 's' : ''}
                  </th>
                ))}
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wide">Renew/yr</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={6} className="py-12 text-center text-slate-400 text-sm">No TLDs found</td></tr>
              ) : (
                filtered.map((t, i) => {
                  const getYearPrice = (yr) => {
                    const row = t.yearly_pricing?.find(r => r.year === yr);
                    return row ? row.selling_price : t.selling_price.add * yr;
                  };
                  return (
                    <tr key={t.tld} className={`border-b border-slate-50 hover:bg-slate-50/70 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <Globe className="w-3.5 h-3.5 text-slate-400" />
                          <span className="font-mono font-semibold text-slate-800 text-sm">.{t.tld}</span>
                          {t.popular && (
                            <span className="text-[9px] font-bold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full uppercase">Popular</span>
                          )}
                        </div>
                      </td>
                      {yearCols.map(yr => (
                        <td key={yr} className="px-4 py-3 text-right">
                          <span className={`text-sm font-semibold ${yr === 1 ? 'text-slate-800' : 'text-slate-600'}`}>
                            {fmt(getYearPrice(yr))}
                          </span>
                        </td>
                      ))}
                      <td className="px-4 py-3 text-right text-sm font-semibold text-slate-600">
                        {fmt(t.selling_price.renew)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Tab 3: Email & WHOIS Pricing Settings ─────────────────────────────────────
function EmailWhoisSettingsTab() {
  const [settings, setSettings]   = useState({
    email_price_per_account: 360,
    email_original_price: 900,
    whois_privacy_price: 299,
    domain_protector_price: 199,
    allow_partner_email_pricing: true,
    allow_partner_domain_protector_pricing: true,
    email_hosting_features: [],
  });
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [form, setForm]           = useState(null);
  const [syncingRC, setSyncingRC] = useState(false);

  // Audit log state
  const [auditLog, setAuditLog]     = useState([]);
  const [auditOpen, setAuditOpen]   = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/domain-settings`)
      .then(r => {
        setSettings(r.data);
        setForm(r.data);
      })
      .catch(() => toast.error('Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/domain-settings`, {
        email_price_per_account:                parseFloat(form.email_price_per_account),
        email_original_price:                   parseFloat(form.email_original_price),
        whois_privacy_price:                    parseFloat(form.whois_privacy_price),
        domain_protector_price:                 parseFloat(form.domain_protector_price || 199),
        allow_partner_email_pricing:            form.allow_partner_email_pricing,
        allow_partner_domain_protector_pricing: form.allow_partner_domain_protector_pricing,
        email_hosting_features:                 form.email_hosting_features || [],
      });
      setSettings(res.data);
      setForm(res.data);
      toast.success('Pricing settings saved successfully');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleRCSync = async () => {
    setSyncingRC(true);
    try {
      const res = await axios.post(`${API}/admin/domain-settings/sync-rc-protector`);
      const { price, source, message } = res.data;
      setForm(f => ({ ...f, domain_protector_price: price }));
      if (source === 'rc') {
        toast.success(`RC price fetched: ₹${price}/year`);
      } else {
        toast.info(message || 'Using stored price — RC API unreachable');
      }
    } catch {
      toast.error('RC sync failed');
    } finally {
      setSyncingRC(false);
    }
  };

  const fetchAuditLog = async () => {
    setAuditLoading(true);
    try {
      const res = await axios.get(`${API}/admin/partner-email-pricing-history`);
      setAuditLog(res.data || []);
    } catch { toast.error('Failed to load audit log'); }
    finally { setAuditLoading(false); }
  };

  const toggleAudit = () => {
    if (!auditOpen) fetchAuditLog();
    setAuditOpen(p => !p);
  };

  // Feature list helpers
  const DEFAULT_FEATURES = [
    "Professional @yourdomain.com email addresses",
    "Spam & virus protection included",
    "IMAP / POP3 / SMTP support",
    "Webmail access from any browser",
    "99.9% uptime guarantee",
  ];
  const addFeature    = () => setForm(f => ({ ...f, email_hosting_features: [...(f.email_hosting_features || []), ''] }));
  const removeFeature = (i) => setForm(f => ({ ...f, email_hosting_features: f.email_hosting_features.filter((_, idx) => idx !== i) }));
  const updateFeature = (i, val) => setForm(f => ({ ...f, email_hosting_features: f.email_hosting_features.map((x, idx) => idx === i ? val : x) }));
  const resetFeatures = () => setForm(f => ({ ...f, email_hosting_features: [...DEFAULT_FEATURES] }));

  if (loading || !form) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin h-7 w-7 rounded-full border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const previewYearly  = parseFloat(form.email_price_per_account) || 0;
  const savePct        = form.email_original_price > 0
    ? Math.round((1 - form.email_price_per_account / form.email_original_price) * 100)
    : 0;

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-0.5">Email & WHOIS Pricing Configuration</h3>
        <p className="text-xs text-slate-400">
          These prices are shown in the Domain Request modal for all users. Set competitive pricing to maximise conversions.
        </p>
      </div>

      {/* Email Hosting Pricing */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-2 mb-1">
          <Mail className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-semibold text-slate-800">Email Hosting Pricing</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1.5">
              Selling Price (₹/account/year)
            </label>
            <Input
              type="number" min="0" step="1"
              value={form.email_price_per_account}
              onChange={e => setForm(f => ({ ...f, email_price_per_account: e.target.value }))}
              className="h-10 text-sm"
              data-testid="email-price-input"
            />
            <p className="text-xs text-slate-400 mt-1">Actual price charged per account per year</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1.5">
              Original / Crossed-Out Price (₹/account/year)
            </label>
            <Input
              type="number" min="0" step="1"
              value={form.email_original_price}
              onChange={e => setForm(f => ({ ...f, email_original_price: e.target.value }))}
              className="h-10 text-sm"
              data-testid="email-original-price-input"
            />
            <p className="text-xs text-slate-400 mt-1">Shown as strikethrough to show savings</p>
          </div>
        </div>

        {/* Preview */}
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
          <p className="text-xs font-semibold text-blue-700 mb-2 uppercase tracking-wide">Modal Preview</p>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-slate-400 line-through">
              ₹{form.email_original_price}/account/year
            </span>
            <span className="text-base font-bold text-green-700">
              ₹{form.email_price_per_account}/account/year
            </span>
            {savePct > 0 && (
              <span className="text-[11px] font-bold bg-green-600 text-white px-2 py-0.5 rounded-full">
                SAVE {savePct}%
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-1.5">
            e.g. 5 accounts × ₹{form.email_price_per_account}/yr = <strong>₹{5 * previewYearly}/year</strong>
          </p>
        </div>
      </div>

      {/* WHOIS Privacy Pricing */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-semibold text-slate-800">WHOIS Privacy Pricing</span>
        </div>

        <div className="max-w-xs">
          <label className="text-xs font-semibold text-slate-600 block mb-1.5">
            WHOIS Privacy Price (₹/year)
          </label>
          <Input
            type="number" min="0" step="1"
            value={form.whois_privacy_price}
            onChange={e => setForm(f => ({ ...f, whois_privacy_price: e.target.value }))}
            className="h-10 text-sm"
            data-testid="whois-price-input"
          />
          <p className="text-xs text-slate-400 mt-1">
            Displayed as "+₹{form.whois_privacy_price}/year" badge on WHOIS Privacy option
          </p>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-blue-500" />
          <span className="text-sm text-slate-700">WHOIS Privacy</span>
          <span className="ml-1 text-xs font-semibold text-blue-600 bg-blue-50 border border-blue-100 rounded-full px-2 py-0.5">
            +₹{form.whois_privacy_price}/year
          </span>
        </div>
      </div>

      {/* Domain Protector+ Pricing */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-semibold text-slate-800">Domain Protector+ Pricing</span>
          </div>
          <button
            type="button"
            onClick={handleRCSync}
            disabled={syncingRC}
            data-testid="sync-rc-protector-btn"
            className="flex items-center gap-1.5 text-xs border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-50 hover:border-blue-300 text-slate-600 hover:text-blue-700 transition-colors disabled:opacity-50"
          >
            {syncingRC ? (
              <span className="animate-spin w-3 h-3 border-b border-blue-600 rounded-full inline-block" />
            ) : (
              <RefreshCw className="w-3 h-3" />
            )}
            Sync from RC
          </button>
        </div>
        <p className="text-xs text-slate-400">
          Protects domains from unauthorized transfers and hijacking. Click "Sync from RC" to attempt auto-fetching the ResellerClub price, or set manually.
        </p>

        <div className="max-w-xs">
          <label className="text-xs font-semibold text-slate-600 block mb-1.5">
            Domain Protector+ Price (₹/year)
          </label>
          <Input
            type="number" min="0" step="1"
            value={form.domain_protector_price ?? 199}
            onChange={e => setForm(f => ({ ...f, domain_protector_price: e.target.value }))}
            className="h-10 text-sm"
            data-testid="domain-protector-price-input"
          />
          <p className="text-xs text-slate-400 mt-1">
            Displayed as "+₹{form.domain_protector_price ?? 199}/year" badge in the request modal
          </p>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-center gap-2">
          <Lock className="w-4 h-4 text-blue-600" />
          <span className="text-sm text-slate-700">Domain Protector+</span>
          <span className="ml-1 text-xs font-semibold text-blue-600 bg-blue-50 border border-blue-100 rounded-full px-2 py-0.5">
            +₹{form.domain_protector_price ?? 199}/year
          </span>
        </div>

        {/* Partner Domain Protector+ Pricing Toggle */}
        <div className="border-t border-slate-100 pt-4">
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, allow_partner_domain_protector_pricing: !f.allow_partner_domain_protector_pricing }))}
              data-testid="allow-partner-dp-pricing-toggle"
              className={`w-11 h-6 rounded-full transition-all relative flex-shrink-0 ${
                form.allow_partner_domain_protector_pricing ? 'bg-blue-600' : 'bg-slate-300'
              }`}
            >
              <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${
                form.allow_partner_domain_protector_pricing ? 'left-6' : 'left-1'
              }`} />
            </button>
            <div>
              <span className={`text-sm font-medium ${form.allow_partner_domain_protector_pricing ? 'text-blue-700' : 'text-slate-600'}`}>
                {form.allow_partner_domain_protector_pricing ? 'Partners can set their own Domain Protector+ markup' : 'All partners use admin price only'}
              </span>
              <p className="text-xs text-slate-400 mt-0.5">
                When enabled, partners can set a selling price for Domain Protector+ in their panel
              </p>
            </div>
          </label>
        </div>
      </div>

      {/* Partner Email Pricing Toggle */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-1">
          <Tag className="w-4 h-4 text-violet-600" />
          <span className="text-sm font-semibold text-slate-800">Partner Email Pricing</span>
        </div>
        <p className="text-xs text-slate-400 mb-4">
          When enabled, partners can set their own email selling price and clients see the partner's price instead of the admin price.
        </p>
        <label className="flex items-center gap-3 cursor-pointer select-none">
          <button
            type="button"
            onClick={() => setForm(f => ({ ...f, allow_partner_email_pricing: !f.allow_partner_email_pricing }))}
            data-testid="allow-partner-pricing-toggle"
            className={`w-11 h-6 rounded-full transition-all relative flex-shrink-0 ${
              form.allow_partner_email_pricing ? 'bg-blue-600' : 'bg-slate-300'
            }`}
          >
            <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${
              form.allow_partner_email_pricing ? 'left-6' : 'left-1'
            }`} />
          </button>
          <div>
            <span className={`text-sm font-medium ${form.allow_partner_email_pricing ? 'text-blue-700' : 'text-slate-600'}`}>
              {form.allow_partner_email_pricing ? 'Partners can set their own email pricing' : 'All partners use admin pricing only'}
            </span>
            <p className="text-xs text-slate-400 mt-0.5">
              {form.allow_partner_email_pricing
                ? 'Partners see an "Email Pricing" section in their Domains page'
                : 'Admin pricing is the single source of truth for all users'}
            </p>
          </div>
        </label>
      </div>

      {/* Email Hosting Features */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span className="text-sm font-semibold text-slate-800">Email Hosting Feature Bullets</span>
            <span className="text-[10px] font-bold bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full uppercase">Admin Only</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={resetFeatures}
              data-testid="reset-features-btn"
              className="text-xs text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg px-2.5 py-1.5 hover:bg-slate-50 transition-colors"
            >
              Reset to Defaults
            </button>
            <Button type="button" size="sm" variant="outline" onClick={addFeature}
              className="h-8 text-xs gap-1" data-testid="add-feature-btn">
              <Plus className="w-3.5 h-3.5" /> Add Feature
            </Button>
          </div>
        </div>
        <p className="text-xs text-slate-400">
          These bullet points appear in the Domain Request modal's email hosting section. Partners cannot edit them.
        </p>
        <div className="space-y-2">
          {(form.email_hosting_features || []).length === 0 ? (
            <p className="text-xs text-slate-400 italic">No features added yet. Click "Add Feature" to start.</p>
          ) : (
            (form.email_hosting_features || []).map((feat, i) => (
              <div key={i} className="flex items-center gap-2">
                <CheckCircle className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                <Input
                  value={feat}
                  onChange={e => updateFeature(i, e.target.value)}
                  placeholder={`Feature ${i + 1}`}
                  className="h-9 text-sm flex-1"
                  data-testid={`feature-input-${i}`}
                />
                <button type="button" onClick={() => removeFeature(i)}
                  className="text-slate-300 hover:text-red-500 transition-colors flex-shrink-0"
                  data-testid={`remove-feature-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Save button */}
      <div className="flex items-center gap-3">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
          data-testid="save-domain-settings-btn"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving…' : 'Save Settings'}
        </Button>
        <button
          type="button"
          onClick={() => setForm(settings)}
          className="text-sm text-slate-500 hover:text-slate-700"
        >
          Reset
        </button>
      </div>

      {/* Partner Pricing Audit Log */}
      <div className="border border-slate-200 rounded-xl overflow-hidden">
        <button
          type="button"
          onClick={toggleAudit}
          data-testid="audit-log-toggle"
          className="w-full flex items-center justify-between px-5 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-slate-500" />
            <span className="text-sm font-semibold text-slate-700">Partner Email Pricing Audit Log</span>
            {auditLog.length > 0 && (
              <span className="text-[10px] font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                {auditLog.length} change{auditLog.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          {auditOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </button>

        {auditOpen && (
          <div>
            {auditLoading ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="w-5 h-5 animate-spin text-slate-300" />
              </div>
            ) : auditLog.length === 0 ? (
              <div className="py-8 text-center">
                <Users className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                <p className="text-xs text-slate-400">No partner pricing changes yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-100 bg-white">
                      <th className="px-4 py-2.5 text-left font-semibold text-slate-600">Partner</th>
                      <th className="px-4 py-2.5 text-left font-semibold text-slate-600">Email</th>
                      <th className="px-4 py-2.5 text-right font-semibold text-slate-600">Old Price</th>
                      <th className="px-4 py-2.5 text-right font-semibold text-slate-600">New Price</th>
                      <th className="px-4 py-2.5 text-right font-semibold text-slate-600">Old Crossed-Out</th>
                      <th className="px-4 py-2.5 text-right font-semibold text-slate-600">New Crossed-Out</th>
                      <th className="px-4 py-2.5 text-left font-semibold text-slate-600">Changed At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLog.map((entry, i) => (
                      <tr key={i} className={`border-b border-slate-50 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}>
                        <td className="px-4 py-2.5 font-medium text-slate-700">{entry.partner_name || '—'}</td>
                        <td className="px-4 py-2.5 text-slate-500">{entry.partner_email || '—'}</td>
                        <td className="px-4 py-2.5 text-right text-slate-400">
                          {entry.old_selling != null ? `₹${entry.old_selling}` : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-right font-semibold text-green-700">₹{entry.new_selling}</td>
                        <td className="px-4 py-2.5 text-right text-slate-400">
                          {entry.old_original != null ? `₹${entry.old_original}` : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-right text-slate-500">₹{entry.new_original}</td>
                        <td className="px-4 py-2.5 text-slate-400">
                          {entry.changed_at ? new Date(entry.changed_at).toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function DomainRegistrationAdmin() {
  const [activeTab, setActiveTab] = useState('tld-pricing');

  const TABS = [
    { id: 'tld-pricing',     label: 'TLD Pricing',             icon: Tag },
    { id: 'available-tlds',  label: 'Available TLDs & Pricing', icon: Globe },
    { id: 'email-settings',  label: 'Email & WHOIS Settings',   icon: Mail },
  ];

  return (
    <div className="space-y-5">
      {/* Tab navigation */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-xl w-fit">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            data-testid={`tab-${id}`}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === id
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'tld-pricing'    && <TldPricingTab />}
      {activeTab === 'available-tlds' && <AvailableTldsTab />}
      {activeTab === 'email-settings' && <EmailWhoisSettingsTab />}
    </div>
  );
}
